<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import { UiDialog, UiInputField, UiSelect, UiText } from '@shopexperts/shared';
import type { AdminCategory } from '@shopexperts/legacy-api';

export type DialogMode = 'create-category' | 'edit-category' | 'create-subcategory' | 'edit-subcategory';

const props = defineProps<{
  mode: DialogMode;
  categories: AdminCategory[];
  initialName?: string;
  initialGroupName?: string | null;
  initialCategoryId?: number;
}>();

const open = defineModel<boolean>({ default: false });
const isLoading = defineModel<boolean>('loading', { default: false });

const emit = defineEmits<{
  submit: [data: { name: string; groupName?: string | null; categoryId?: number }];
}>();

const name = ref('');
const groupName = ref('');
const selectedCategoryId = ref<string>('');
const nameError = ref('');
const groupNameError = ref('');

const isSubcategoryMode = computed(() =>
  props.mode === 'create-subcategory' || props.mode === 'edit-subcategory'
);
const isCategoryMode = computed(() => !isSubcategoryMode.value);

const title = computed(() => {
  switch (props.mode) {
    case 'create-category': return 'Add Category';
    case 'edit-category': return 'Edit Category';
    case 'create-subcategory': return 'Add Subcategory';
    case 'edit-subcategory': return 'Edit Subcategory';
  }
});

const submitLabel = computed(() => {
  switch (props.mode) {
    case 'create-category':
    case 'create-subcategory':
      return 'Create';
    case 'edit-category':
    case 'edit-subcategory':
      return 'Save Changes';
  }
});

const categoryOptions = computed(() =>
  props.categories.map((cat) => ({
    label: cat.name,
    value: String(cat.id),
  }))
);

watch(open, (isOpen) => {
  if (isOpen) {
    name.value = props.initialName ?? '';
    groupName.value = props.initialGroupName ?? '';
    selectedCategoryId.value = props.initialCategoryId ? String(props.initialCategoryId) : '';
    nameError.value = '';
    groupNameError.value = '';
  }
});

function validate(): boolean {
  const trimmed = name.value.trim();
  if (!trimmed) {
    nameError.value = 'Name is required.';
    return false;
  }
  if (trimmed.length > 255) {
    nameError.value = 'Name must be 255 characters or less.';
    return false;
  }
  const trimmedGroupName = groupName.value.trim();
  if (isCategoryMode.value && trimmedGroupName.length > 255) {
    groupNameError.value = 'Group must be 255 characters or less.';
    return false;
  }
  if (isSubcategoryMode.value && !selectedCategoryId.value) {
    nameError.value = 'Please select a parent category.';
    return false;
  }
  nameError.value = '';
  groupNameError.value = '';
  return true;
}

function handleSubmit() {
  if (!validate()) return;
  const trimmedGroupName = groupName.value.trim();
  const data: { name: string; groupName?: string | null; categoryId?: number } = {
    name: name.value.trim(),
  };
  if (isCategoryMode.value) {
    data.groupName = trimmedGroupName || null;
  }
  if (isSubcategoryMode.value) {
    data.categoryId = Number(selectedCategoryId.value);
  }
  emit('submit', data);
}
</script>

<template>
  <UiDialog
    v-model="open"
    v-model:loading="isLoading"
    :title="title"
    :primaryActionLabel="submitLabel"
    secondaryActionLabel="Cancel"
    size="sm"
    @primaryAction="handleSubmit"
    @secondaryAction="open = false"
    @close="open = false"
  >
    <div class="flex flex-col gap-4">
      <div>
        <UiText as="label" variant="caption-normal" class="text-gray-600 mb-1.5 block font-medium">
          Name
        </UiText>
        <UiInputField
          v-model="name"
          placeholder="Enter name..."
          size="md"
          :error="nameError || undefined"
          :invalid="!!nameError"
          @keydown.enter.prevent="handleSubmit"
        />
      </div>

      <div v-if="isCategoryMode">
        <UiText as="label" variant="caption-normal" class="text-gray-600 mb-1.5 block font-medium">
          Group
        </UiText>
        <UiInputField
          v-model="groupName"
          placeholder="Enter group..."
          size="md"
          :error="groupNameError || undefined"
          :invalid="!!groupNameError"
          @keydown.enter.prevent="handleSubmit"
        />
      </div>

      <div v-if="isSubcategoryMode">
        <UiText as="label" variant="caption-normal" class="text-gray-600 mb-1.5 block font-medium">
          Parent Category
        </UiText>
        <UiSelect
          v-model="selectedCategoryId"
          :options="categoryOptions"
          placeholder="Select a category..."
          size="md"
        />
      </div>
    </div>
  </UiDialog>
</template>
